import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, Forward, Loader2, Search, X } from 'lucide-react';
import { getUnifiedInbox, searchMessages } from '@/api/inbox';
import { getThread } from '@/api/messages';
import { handleTrapKey } from '@/lib/focus-trap';
import { formatDate, formatDateTime } from '@/lib/utils';
import type { Message } from '@/types';

// "Forward an email to this address" (2026-10-19, Brian): browse the inbox, pick a message, then
// forward that message or its whole conversation.
//
// 🚩 Conversations are the subject-grouped `threads` sync keeps (backend mail/thread-members.js),
// so one can hold unrelated mail with the same subject. The second step lists every message that
// will go before anything is sent, and says when only the most recent are included.

export interface ForwardPick { ref: string; thread: boolean }

const refOf = (m: Pick<Message, 'mailboxId' | 'uid' | 'folder'>) => `${m.mailboxId}/${m.uid}/${m.folder || 'INBOX'}`;
const who = (m: { fromName?: string | null; fromEmail?: string | null }) => m.fromName || m.fromEmail || 'Unknown sender';

export function ForwardPickerModal({ to, onClose, onPick }: { to: string; onClose: () => void; onPick: (p: ForwardPick) => void }) {
  const panel = useRef<HTMLDivElement>(null);
  // The parent passes a fresh onClose each render; focus is placed once, on open, not on every render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const searchBox = useRef<HTMLInputElement>(null);
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Message | null>(null);
  const backButton = useRef<HTMLButtonElement>(null);
  // The row that was clicked is gone once a message is picked; keep focus inside the dialog.
  useEffect(() => { (picked ? backButton.current : searchBox.current)?.focus(); }, [picked]);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    searchBox.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { closeRef.current(); return; }
      handleTrapKey(e, panel.current);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
  }, []);

  // Search as you type, a beat after the last key.
  useEffect(() => {
    const t = setTimeout(() => setQuery(typed.trim()), 250);
    return () => clearTimeout(t);
  }, [typed]);

  const list = useQuery({
    queryKey: ['forward-picker', query],
    queryFn: () => (query ? searchMessages(query, 'all') : getUnifiedInbox('all')),
  });
  const messages = list.data?.messages ?? [];

  // How many of the listed messages share each conversation — a hint on the row only; the
  // second step asks the server for the real membership.
  const inThread = useMemo(() => {
    const n = new Map<string, number>();
    for (const m of messages) if (m.threadId) n.set(`${m.mailboxId}:${m.threadId}`, (n.get(`${m.mailboxId}:${m.threadId}`) || 0) + 1);
    return n;
  }, [messages]);

  const thread = useQuery({
    queryKey: ['thread', picked && refOf(picked)],
    queryFn: () => getThread(picked!.mailboxId, picked!.uid, picked!.folder),
    enabled: !!picked,
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-[2px]"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={panel} role="dialog" aria-modal="true" aria-label={`Forward an email to ${to}`} tabIndex={-1}
        className="flex max-h-[min(40rem,calc(100vh-2rem))] w-full max-w-lg flex-col rounded-2xl border border-bmail-border bg-bmail-surface shadow-2xl focus:outline-none">
        <div className="flex items-center gap-2 border-b border-bmail-border px-4 py-3">
          {picked && (
            <button ref={backButton} onClick={() => setPicked(null)} aria-label="Back to the list"
              className="rounded p-1 text-bmail-text-secondary hover:text-bmail-text">
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-semibold text-bmail-text">Forward an email</h3>
            <p className="truncate text-xs text-bmail-text-secondary">to {to}</p>
          </div>
          <button onClick={onClose} aria-label="Close" className="rounded p-1 text-bmail-text-secondary hover:text-bmail-text">
            <X className="h-4 w-4" />
          </button>
        </div>

        {!picked ? (
          <>
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 rounded-lg border border-bmail-border bg-bmail-bg px-3 focus-within:ring-2 focus-within:ring-bmail-gold">
                <Search className="h-4 w-4 shrink-0 text-bmail-text-secondary" />
                <input ref={searchBox} value={typed} onChange={(e) => setTyped(e.target.value)}
                  placeholder="Search your inbox" aria-label="Search your inbox"
                  className="w-full bg-transparent py-2 text-sm text-bmail-text focus:outline-none" />
              </div>
            </div>
            <ul className="min-h-[8rem] flex-1 overflow-y-auto px-2 pb-2" aria-label="Messages">
              {list.isLoading && <li className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-bmail-text-secondary" /></li>}
              {list.isError && <li className="px-3 py-6 text-center text-sm text-bmail-error">Couldn’t load your inbox. Try again.</li>}
              {!list.isLoading && !list.isError && messages.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-bmail-text-secondary">{query ? 'Nothing matches that search.' : 'Your inbox is empty.'}</li>
              )}
              {messages.map((m) => {
                const n = m.threadId ? inThread.get(`${m.mailboxId}:${m.threadId}`) || 1 : 1;
                return (
                  <li key={m.id}>
                    <button onClick={() => setPicked(m)} data-ref={refOf(m)}
                      className="flex w-full flex-col gap-0.5 rounded-lg px-3 py-2 text-left hover:bg-bmail-bg focus:bg-bmail-bg focus:outline-none">
                      <span className="flex w-full items-baseline gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-bmail-text">{who(m)}</span>
                        <span className="shrink-0 text-xs text-bmail-text-secondary">{formatDate(m.date)}</span>
                      </span>
                      <span className="flex w-full items-baseline gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm text-bmail-text-secondary">{m.subject || '(no subject)'}</span>
                        {n > 1 && <span className="shrink-0 rounded-full bg-bmail-bg px-1.5 text-xs text-bmail-text-secondary">{n}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
            <div className="rounded-lg border border-bmail-border bg-bmail-bg px-3 py-2">
              <div className="truncate text-sm font-medium text-bmail-text">{picked.subject || '(no subject)'}</div>
              <div className="truncate text-xs text-bmail-text-secondary">{who(picked)} · {formatDateTime(picked.date)}</div>
            </div>
            <button onClick={() => onPick({ ref: refOf(picked), thread: false })}
              className="flex items-center justify-center gap-2 rounded-lg bg-bmail-gold px-3 py-2 text-sm font-semibold text-black hover:opacity-90">
              <Forward className="h-4 w-4" /> Forward this message
            </button>
            {thread.isLoading && <div className="flex justify-center py-2"><Loader2 className="h-4 w-4 animate-spin text-bmail-text-secondary" /></div>}
            {thread.data && thread.data.total > 1 && (
              <div className="flex flex-col gap-2">
                <button onClick={() => onPick({ ref: refOf(picked), thread: true })}
                  className="flex items-center justify-center gap-2 rounded-lg border border-bmail-border px-3 py-2 text-sm text-bmail-text hover:border-bmail-gold">
                  <Forward className="h-4 w-4" /> Forward the whole conversation ({thread.data.members.length})
                </button>
                <p className="text-xs text-bmail-text-secondary">
                  {thread.data.total > thread.data.members.length
                    ? `${thread.data.members.length} of the ${thread.data.total} messages with this subject will be quoted (this one and the most recent). Choose whether to include attachments when composing:`
                    : 'These messages will be quoted. Choose whether to include attachments when composing:'}
                </p>
                <ol className="flex flex-col gap-1" aria-label="Messages in this conversation">
                  {thread.data.members.map((t) => (
                    <li key={`${t.folder}/${t.uid}`} className="flex items-baseline gap-2 text-xs">
                      <span className="min-w-0 flex-1 truncate text-bmail-text">{who(t)}</span>
                      <span className="shrink-0 text-bmail-text-secondary">{formatDateTime(t.date)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
